import moment from "moment";
import { POST_WINDOW_RATES } from "../api/kpi";
import { companyToday } from "./companyTime";
import { isWindowRateKpi } from "./scoreAggregate";

/** Home score buckets that need on-demand rate recompute (not "this month"). */
export const SHORT_RATE_WINDOWS = ["today", "yesterday", "week", "lastWeek"];

/** True when the board window should call /kpi/window-rates (not month snapshot). */
export const needsWindowRates = (win) => {
  const w = String(win || "month");
  return w !== "month" && w !== "year" && w !== "ytd" && w !== "quarter";
};

/**
 * Stamp server window-rate cells onto a per-kpi score bucket.
 * @param {object} bucket  { [kpiId]: { amt, pts, ... } }
 * @param {object} ownerRates  { [kpiId]: { amt, pts } }
 */
export const mergeWindowRatesIntoBucket = (bucket, ownerRates) => {
  if (!bucket || !ownerRates) return bucket;
  Object.entries(ownerRates).forEach(([kpiId, cell]) => {
    if (!cell || typeof cell !== "object") return;
    bucket[kpiId] = {
      ...(bucket[kpiId] || {}),
      amt: Number(cell.amt) || 0,
      pts: Number(cell.pts) || 0,
      scored: true,
      windowRate: true,
    };
  });
  return bucket;
};

/**
 * Overlay window rates onto each player's named bucket (today/week/…).
 * @param {Array} usersKpis  [{ userId, today, week, … }]
 * @param {object} ratesByOwner  { [ownerId]: { [kpiId]: { amt, pts } } }
 * @param {string} bucketName
 */
export const mergeWindowRatesIntoUsers = (usersKpis, ratesByOwner, bucketName) => {
  if (!ratesByOwner || !bucketName) return usersKpis;
  (usersKpis || []).forEach((u) => {
    const ownerId = String(u.userId || u.ownerId || "");
    const rates = ratesByOwner[ownerId];
    if (!rates || !u[bucketName]) return;
    mergeWindowRatesIntoBucket(u[bucketName], rates);
  });
  return usersKpis;
};

/**
 * Overlay onto readWorkspace-style { perUser } maps.
 */
export const applyWindowRatesToPerUser = (perUser, ratesByOwner, metaById = null) => {
  if (!perUser || !ratesByOwner) return perUser;
  Object.entries(ratesByOwner).forEach(([ownerId, rates]) => {
    if (!perUser[ownerId]) {
      perUser[ownerId] = { kpis: {}, pts: 0 };
    }
    mergeWindowRatesIntoBucket(perUser[ownerId].kpis, rates);
  });
  if (metaById) {
    Object.values(perUser).forEach((u) => {
      Object.keys(u.kpis || {}).forEach((kpiId) => {
        if (!isWindowRateKpi(metaById[kpiId]) || u.kpis[kpiId]?.windowRate) return;
      });
    });
  }
  return perUser;
};

/** Local window bounds — do not import useWorkspaceData (import/no-cycle). */
const resolveWindowLocal = (win, custom) => {
  const today = companyToday();
  if (win === "custom") {
    const from = custom?.from ? moment(custom.from).startOf("day") : today.clone().startOf("month");
    let to = custom?.to ? moment(custom.to).endOf("day") : today.clone().endOf("day");
    const endToday = today.clone().endOf("day");
    if (to.isAfter(endToday)) to = endToday.clone();
    if (from.isAfter(endToday)) {
      return { from: endToday.clone().add(1, "ms"), to: endToday.clone() };
    }
    return { from, to };
  }
  if (win === "today") return { from: today.clone().startOf("day"), to: today.clone().endOf("day") };
  if (win === "yesterday") {
    const y = today.clone().subtract(1, "day");
    return { from: y.clone().startOf("day"), to: y.clone().endOf("day") };
  }
  if (win === "week") {
    return { from: today.clone().startOf("week"), to: today.clone().endOf("day") };
  }
  if (win === "lastWeek") {
    const start = today.clone().subtract(1, "week").startOf("week");
    return { from: start, to: start.clone().endOf("week") };
  }
  if (win === "lastMonth") {
    const start = today.clone().subtract(1, "month").startOf("month");
    return { from: start, to: start.clone().endOf("month") };
  }
  if (win === "quarter") return { from: today.clone().startOf("quarter"), to: today.clone().endOf("day") };
  if (win === "ytd" || win === "year") {
    return { from: today.clone().startOf("year"), to: today.clone().endOf("day") };
  }
  return { from: today.clone().startOf("month"), to: today.clone().endOf("day") };
};

/** YYYY-MM-DD bounds for a named window (company calendar). */
export const boundsForWindow = (win, custom = null) => {
  const { from, to } = resolveWindowLocal(win === "year" ? "ytd" : win, custom);
  return {
    from: from?.format?.("YYYY-MM-DD") || String(from || "").slice(0, 10),
    to: to?.format?.("YYYY-MM-DD") || String(to || "").slice(0, 10),
  };
};

/**
 * Fetch window rates for one workspace + window. Returns {} on failure.
 * Response shape: { [ownerId]: { [kpiId]: { amt, pts } } }
 */
// cacheKey → { at, rates }. The Team card (useScopedMetrics), the
// Leaderboard card (useLeaderboardData), and Home's per-player cards
// (useKpiData) each ask for the SAME (groupId, from, to) window
// independently — without this, one page render fired 2-3+ duplicate
// POST /kpi/window-rates round trips for identical data, which is what
// made Today/Yesterday/Week/LastWeek feel slow to settle. Short TTL: window
// rates change as often as the HCP sync does (~1 min prod), so this only
// needs to survive the handful of cards mounting within the same tick.
const windowRatesCache = new Map();
const inflightWindowRates = new Map();
const WINDOW_RATES_CACHE_MS = 15 * 1000;

export const fetchWindowRates = async ({ groupId, from, to, kpiIds } = {}) => {
  if (!from || !to) return {};
  const cacheKey = `${groupId || ""}|${from}|${to}|${Array.isArray(kpiIds) ? kpiIds.slice().sort().join(",") : ""}`;
  const cached = windowRatesCache.get(cacheKey);
  if (cached && Date.now() - cached.at < WINDOW_RATES_CACHE_MS) return cached.rates;

  const pending = inflightWindowRates.get(cacheKey);
  if (pending) return pending;

  const req = (async () => {
    try {
      const res = await POST_WINDOW_RATES({ groupId, from, to, kpiIds });
      const data = res?.data ?? res;
      const rates = data && typeof data === "object" && !Array.isArray(data) ? data : {};
      windowRatesCache.set(cacheKey, { at: Date.now(), rates });
      return rates;
    } catch {
      return {};
    } finally {
      inflightWindowRates.delete(cacheKey);
    }
  })();
  inflightWindowRates.set(cacheKey, req);
  return req;
};

/**
 * Prefetch Home short-period buckets and merge into usersKpis (+ optional myKpis).
 */
export const applyShortWindowRates = async ({
  groupId,
  usersKpis,
  myKpis = null,
  myUserId = null,
  windows = SHORT_RATE_WINDOWS,
}) => {
  if (!groupId || !usersKpis?.length) return { usersKpis, myKpis };
  await Promise.all(
    windows.map(async (name) => {
      const { from, to } = boundsForWindow(name);
      if (!from || !to) return;
      const ratesByOwner = await fetchWindowRates({ groupId, from, to });
      mergeWindowRatesIntoUsers(usersKpis, ratesByOwner, name);
      if (myKpis && myUserId && myKpis[name]) {
        mergeWindowRatesIntoBucket(myKpis[name], ratesByOwner[String(myUserId)]);
      }
    }),
  );
  return { usersKpis, myKpis };
};

/**
 * Overlay window-rate cells onto a readWorkspace slice and rebuild byKpi.
 */
export const overlayWindowRatesOnSlice = (slice, ratesByOwner) => {
  if (!slice || !ratesByOwner) return slice;
  const players = (slice.players || []).map((p) => {
    const ownerId = String(p.userId || p.ownerId || "");
    const rates = ratesByOwner[ownerId];
    if (!rates) return p;
    const kpis = { ...(p.kpis || {}) };
    mergeWindowRatesIntoBucket(kpis, rates);
    const pts = Object.values(kpis).reduce((s, c) => s + (Number(c?.pts) || 0), 0);
    return { ...p, kpis, pts };
  });

  const byKpi = { ...(slice.byKpi || {}) };
  const touched = new Set();
  Object.values(ratesByOwner).forEach((rates) => {
    Object.keys(rates || {}).forEach((id) => touched.add(String(id)));
  });
  touched.forEach((kpiId) => {
    let sum = 0;
    let pts = 0;
    let scorers = 0;
    players.forEach((p) => {
      const cell = p.kpis?.[kpiId];
      if (!cell) return;
      const amt = Number(cell.amt);
      if (!Number.isFinite(amt)) return;
      if (!cell.windowRate && !(amt > 0)) return;
      sum += amt;
      pts += Number(cell.pts) || 0;
      scorers += 1;
    });
    if (!scorers) {
      delete byKpi[kpiId];
      return;
    }
    byKpi[kpiId] = {
      ...(byKpi[kpiId] || {}),
      sum,
      pts,
      scorers,
      avg: sum / scorers,
      avgPts: pts / scorers,
      scored: true,
      defaultPerfect: false,
    };
  });

  const teamPts = players.reduce((n, p) => n + (Number(p.pts) || 0), 0);
  return { ...slice, players, byKpi, teamPts };
};
